/**
 * LanguageSelector — Phase 8D Wave 1 Day 2.
 *
 * Compact dropdown rendered in the user-profile area. Switches active
 * language via i18n setLanguage(), which:
 *   - lazy-loads the locale bundle (skipped if already loaded or 'en')
 *   - persists to localStorage under 'ryedore.language'
 *   - updates <html lang> for a11y
 *
 * The selector itself uses the 'common' namespace (so its own labels
 * translate). It's safe to render before login since SUPPORTED_LANGUAGES
 * + LANGUAGE_NAMES are static.
 */
import React, { useState, useRef, useEffect } from 'react'
import { Globe, Check, ChevronDown } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, setLanguage, type SupportedLanguage } from '../../i18n/config'

interface Props {
  compact?: boolean
  className?: string
}

export const LanguageSelector: React.FC<Props> = ({ compact = false, className }) => {
  const { i18n, t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const onPick = async (lang: SupportedLanguage) => {
    setOpen(false)
    if (lang === current) return
    try {
      await setLanguage(lang)
    } catch (e) {
      console.error('Language switch failed:', e)
    }
  }

  return (
    <div ref={ref} className={clsx('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={t('language.selector_label', 'Language')}
        className={clsx(
          'flex items-center gap-1.5 rounded-md text-sm',
          'text-gray-300 hover:text-white hover:bg-gray-700/50',
          'transition-colors',
          compact ? 'px-2 py-1' : 'px-3 py-1.5',
        )}
      >
        <Globe size={16} />
        {!compact && <span>{LANGUAGE_NAMES[current]}</span>}
        <ChevronDown size={14} className={clsx('transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="menu"
          className={clsx(
            'absolute right-0 mt-1 z-50 min-w-[160px]',
            'rounded-md border border-gray-700 bg-gray-800 shadow-lg',
            'py-1',
          )}
        >
          {SUPPORTED_LANGUAGES.map(lang => (
            <button
              key={lang}
              type="button"
              role="menuitem"
              onClick={() => onPick(lang)}
              className={clsx(
                'flex w-full items-center justify-between px-3 py-1.5 text-sm',
                'text-gray-200 hover:bg-gray-700',
                lang === current && 'bg-gray-700/60',
              )}
            >
              <span>{LANGUAGE_NAMES[lang]}</span>
              {lang === current && <Check size={14} className="text-blue-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSelector
